// Single source of truth for the Research Support Center (FAQ).
// Add / edit / reorder / deactivate entries here without touching the page component.
// Each entry: { id, category, q, a, keywords, links, active }
// Keep every answer research-oriented and RUO-compliant. Do NOT add human-use,
// medical, dosing, or unsupported business claims.

export const FAQ_CATEGORIES = [
  "Research Use",
  "Products & Specifications",
  "Documentation",
  "Storage & Handling",
  "Ordering",
  "Payment",
  "Shipping",
  "Returns & Issues",
  "Account & Privacy",
  "Compliance",
  "Institutional / Wholesale",
  "Contact & Support",
];

export const FAQS = [
  // ---------------- Research Use ----------------
  { id: "ru-intended", category: "Research Use", active: true,
    q: "What are Molecularix Biolabs products intended for?",
    a: "All Molecularix Biolabs products are supplied exclusively for legitimate in-vitro laboratory and research applications. They are reference materials for research use only.",
    keywords: ["purpose", "use", "research", "in-vitro", "laboratory"],
    links: [{ label: "Research Use Only Policy", to: "/policy/research-use" }] },
  { id: "ru-human", category: "Research Use", active: true,
    q: "Are Molecularix products for human use?",
    a: "No. Our products are not drugs, foods, cosmetics, or supplements. They are not intended for human consumption, administration, or any in-vivo use.",
    keywords: ["human", "consumption", "not for human", "safe"],
    links: [{ label: "Research Use Only Policy", to: "/policy/research-use" }] },
  { id: "ru-vet", category: "Research Use", active: true,
    q: "Are Molecularix products for veterinary use?",
    a: "No. Products are not intended for veterinary or animal use of any kind. They are supplied strictly for in-vitro research.",
    keywords: ["veterinary", "animal", "pet"] },
  { id: "ru-meaning", category: "Research Use", active: true,
    q: "What does \u201cResearch Use Only\u201d mean?",
    a: "Research Use Only (RUO) means a material is intended solely for laboratory research and not for diagnostic, therapeutic, or consumer use. The purchaser is responsible for handling the material lawfully and safely in an appropriate research setting.",
    keywords: ["ruo", "research use only", "meaning", "definition"],
    links: [{ label: "Read the RUO Policy", to: "/policy/research-use" }] },
  { id: "ru-who", category: "Research Use", active: true,
    q: "Who can purchase research materials?",
    a: "Purchases are intended for qualified researchers and research, university, biotech, or analytical laboratory accounts. Buyers must confirm they are of legal age and will use the materials only for in-vitro research.",
    keywords: ["who", "buy", "purchase", "eligible", "qualified"] },
  { id: "ru-gate", category: "Research Use", active: true,
    q: "Why is an eligibility verification required?",
    a: "Access to the catalog requires an attestation of legal age and legitimate research use. This helps ensure materials are supplied only for their intended in-vitro research purpose, consistent with our compliance stance.",
    keywords: ["eligibility", "gate", "verify", "age", "21", "attestation"],
    links: [{ label: "Our Standards", to: "/standards" }] },

  // ---------------- Products & Specifications ----------------
  { id: "ps-info", category: "Products & Specifications", active: true,
    q: "What information is provided for each compound?",
    a: "Each product page lists the compound name, SKU, strength (mg per vial), research category, physical form, and specification fields such as CAS number, sequence, and molecular details where applicable, along with availability and documentation status.",
    keywords: ["information", "specs", "specification", "details", "cas", "sequence"],
    links: [{ label: "Browse the Catalog", to: "/" }] },
  { id: "ps-mg", category: "Products & Specifications", active: true,
    q: "What does the listed mg amount represent?",
    a: "The milligram value is the stated quantity of reference material contained in the sealed vial. It is displayed on the catalog card and the product page.",
    keywords: ["mg", "milligram", "strength", "quantity", "amount"] },
  { id: "ps-form", category: "Products & Specifications", active: true,
    q: "What form do the compounds come in?",
    a: "Most compounds are supplied as lyophilized (freeze-dried) material in sealed vials. The physical form is indicated on each product page.",
    keywords: ["form", "lyophilized", "powder", "freeze dried", "vial"] },
  { id: "ps-lyo", category: "Products & Specifications", active: true,
    q: "Are products supplied as lyophilized materials?",
    a: "Yes, in most cases products are supplied lyophilized in sealed vials. Refer to the individual product page for the specific form of that item.",
    keywords: ["lyophilized", "freeze dried", "sealed", "vial"] },
  { id: "ps-id", category: "Products & Specifications", active: true,
    q: "How are products identified?",
    a: "Every product has a unique SKU in the format MX-<code>-<strength> (for example, MX-RETATRUT-020), along with its name, category, and specification fields on the product page.",
    keywords: ["identify", "sku", "code", "identifier"] },
  { id: "ps-sku", category: "Products & Specifications", active: true,
    q: "What is a SKU?",
    a: "A SKU (Stock Keeping Unit) is the unique reference code we assign to each catalog item so it can be identified, ordered, and documented consistently. You'll find it on every product page.",
    keywords: ["sku", "what is", "stock keeping unit", "code"] },
  { id: "ps-choose", category: "Products & Specifications", active: true,
    q: "How can I determine which research material is appropriate for my research?",
    a: "Product pages provide the specifications needed to evaluate a material against your research protocol. We can answer questions about specifications and documentation, but we cannot advise on experimental design or provide usage, dosing, or administration guidance.",
    keywords: ["choose", "which", "appropriate", "select", "help"],
    links: [{ label: "Contact Support", to: "/contact" }] },

  // ---------------- Documentation ----------------
  { id: "coa-verify", category: "Documentation", active: true,
    q: "How can I verify a batch or lot?",
    a: "Use the Batch Verification tool and enter your batch/lot number. The system returns the documentation on record for that batch, or clearly indicates when no record is found.",
    keywords: ["verify", "batch", "lot", "verification", "check"],
    links: [{ label: "Verify a Batch", to: "/verify" }] },
  { id: "coa-where", category: "Documentation", active: true,
    q: "Where can I find product documentation?",
    a: "Documentation lives in the Documentation Center and on individual product pages. If a document you need isn't listed, you can request it directly.",
    keywords: ["documentation", "where", "find", "docs"],
    links: [{ label: "Documentation Center", to: "/documentation" }] },
  { id: "coa-every", category: "Documentation", active: true,
    q: "Does every product have batch-specific documentation?",
    a: "Not every item currently has a published batch document. You can request documentation for a specific compound and we'll follow up with what is on file.",
    keywords: ["every", "batch specific", "all products"],
    links: [{ label: "Request Documentation", to: "/documentation" }] },
  { id: "coa-request", category: "Documentation", active: true,
    q: "How do I request documentation that I cannot find?",
    a: "Submit a request through the Documentation Center with your email and the compound or SKU, and our team will respond with the available documentation.",
    keywords: ["request", "cannot find", "missing", "ask for documentation"],
    links: [{ label: "Request Documentation", to: "/documentation" }] },

  // ---------------- Storage & Handling ----------------
  { id: "sh-store", category: "Storage & Handling", active: true,
    q: "How should research materials be stored?",
    a: "Storage guidance is provided on each product page and, where applicable, in the item's documentation. Follow the storage information listed for the specific compound you received.",
    keywords: ["storage", "store", "keep", "temperature"],
    links: [{ label: "Our Standards", to: "/standards" }] },
  { id: "sh-specific", category: "Storage & Handling", active: true,
    q: "Are storage requirements product-specific?",
    a: "Yes. Storage conditions can vary by compound, so always refer to the storage field on the relevant product page rather than assuming a single condition for all items.",
    keywords: ["storage", "product specific", "vary", "different"] },
  { id: "sh-package", category: "Storage & Handling", active: true,
    q: "How are research materials packaged?",
    a: "Materials are supplied in sealed vials and shipped in protective, discreet packaging intended to preserve integrity in transit to your laboratory. We handle materials with temperature and light sensitivity in mind.",
    keywords: ["packaging", "packaged", "discreet", "sealed", "protective"] },
  { id: "sh-damaged", category: "Storage & Handling", active: true,
    q: "What should I do if a shipment arrives damaged?",
    a: "Contact support promptly with your order reference and photos of the packaging and vials. We'll review the issue in line with our Refund Policy.",
    keywords: ["damaged", "broken", "arrived", "problem"],
    links: [{ label: "Contact Support", to: "/contact" }, { label: "Refund Policy", to: "/policy/refunds" }] },
  { id: "sh-excursion", category: "Storage & Handling", active: true,
    q: "What should I do if I suspect a temperature excursion?",
    a: "Note the details and contact support with your order reference. We handle materials with temperature-conscious packaging and will review any suspected transit issue with you.",
    keywords: ["temperature", "excursion", "heat", "cold", "transit"],
    links: [{ label: "Contact Support", to: "/contact" }] },

  // ---------------- Ordering ----------------
  { id: "or-place", category: "Ordering", active: true,
    q: "How do I place an order?",
    a: "Browse the catalog, add compounds to your cart, and complete checkout securely on-site. You'll receive an order confirmation by email.",
    keywords: ["order", "place", "buy", "checkout", "how to"],
    links: [{ label: "Browse the Catalog", to: "/" }] },
  { id: "or-account", category: "Ordering", active: true,
    q: "Do I need to create an account?",
    a: "You can order with your email, and you may also sign in with Google to access your order history, one-tap reorders, and saved cart. An account isn't required to check out.",
    keywords: ["account", "sign in", "register", "login", "google"],
    links: [{ label: "Your Account", to: "/account" }] },
  { id: "or-eligibility", category: "Ordering", active: true,
    q: "How does the eligibility process work?",
    a: "On first visit you confirm you meet the age requirement and that materials are for in-vitro research use only. This attestation is recorded before catalog access is granted.",
    keywords: ["eligibility", "gate", "process", "attestation", "verify"] },
  { id: "or-modify", category: "Ordering", active: true,
    q: "Can I modify an order after placing it?",
    a: "If you need to change an order, contact support as soon as possible with your order reference. Changes may be possible before an order is prepared for shipment.",
    keywords: ["modify", "change", "edit", "cancel order"],
    links: [{ label: "Contact Support", to: "/contact" }] },
  { id: "or-track", category: "Ordering", active: true,
    q: "How can I track my order?",
    a: "Use the Track Order page and enter your order reference and email to see the latest status. Tracking details are added once your order ships.",
    keywords: ["track", "tracking", "status", "where is my order"],
    links: [{ label: "Track Order", to: "/track" }] },
  { id: "or-after", category: "Ordering", active: true,
    q: "What happens after an order is submitted?",
    a: "You receive an email confirmation, your order is prepared, and tracking information is provided once it ships. You can follow progress anytime from the Track Order page.",
    keywords: ["after", "next", "confirmation", "process"],
    links: [{ label: "Track Order", to: "/track" }] },

  // ---------------- Payment ----------------
  { id: "pay-methods", category: "Payment", active: true,
    q: "What payment methods are accepted?",
    a: "Checkout is completed securely on-site by card, processed through Stripe. Applicable sales tax is calculated automatically at checkout.",
    keywords: ["payment", "pay", "card", "stripe", "methods", "credit"] },
  { id: "pay-when", category: "Payment", active: true,
    q: "When is payment processed?",
    a: "Payment is processed securely at checkout through Stripe when you complete your order.",
    keywords: ["payment processed", "when", "charge", "billing"] },
  { id: "pay-invoice", category: "Payment", active: true,
    q: "Are receipts available?",
    a: "Yes. An itemized order confirmation is emailed after a successful order. If you need an additional copy for your records, contact support with your order reference.",
    keywords: ["invoice", "receipt", "records", "itemized"],
    links: [{ label: "Contact Support", to: "/contact" }] },
  { id: "pay-po", category: "Payment", active: true,
    q: "Are institutional purchase orders supported?",
    a: "For institutional or purchase-order inquiries, contact us and we'll discuss what we can accommodate. Standard checkout is by card via Stripe.",
    keywords: ["purchase order", "po", "institutional", "net terms"],
    links: [{ label: "Contact Us", to: "/contact" }] },

  // ---------------- Shipping ----------------
  { id: "sh-where", category: "Shipping", active: true,
    q: "Where does Molecularix ship?",
    a: "Shipping destinations and current terms are described on our Shipping Policy page. Please review it for the most accurate, up-to-date information.",
    keywords: ["ship", "where", "destinations", "international", "domestic"],
    links: [{ label: "Shipping Policy", to: "/policy/shipping" }] },
  { id: "sh-howpack", category: "Shipping", active: true,
    q: "How are orders packaged for shipping?",
    a: "Orders are packaged discreetly and with temperature-conscious handling to help protect materials in transit to your laboratory.",
    keywords: ["packaging", "discreet", "protective", "shipping"] },
  { id: "sh-howlong", category: "Shipping", active: true,
    q: "How long does shipping take?",
    a: "Processing and delivery timeframes are outlined on our Shipping Policy page. You'll also receive tracking so you can follow your shipment.",
    keywords: ["how long", "time", "delivery", "days", "fast"],
    links: [{ label: "Shipping Policy", to: "/policy/shipping" }] },
  { id: "sh-track", category: "Shipping", active: true,
    q: "How do I track my shipment?",
    a: "Enter your order reference and email on the Track Order page. Tracking is available once your order has shipped.",
    keywords: ["track shipment", "tracking number", "follow"],
    links: [{ label: "Track Order", to: "/track" }] },
  { id: "sh-delayed", category: "Shipping", active: true,
    q: "What happens if my shipment is delayed?",
    a: "If your tracking hasn't updated as expected, contact support with your order reference and we'll help look into it.",
    keywords: ["delayed", "late", "stuck", "not moving"],
    links: [{ label: "Contact Support", to: "/contact" }] },
  { id: "sh-shipdamage", category: "Shipping", active: true,
    q: "What happens if my package is damaged in transit?",
    a: "Contact support promptly with your order reference and photos. We'll review the issue in line with our Refund Policy.",
    keywords: ["damaged", "transit", "broken package"],
    links: [{ label: "Refund Policy", to: "/policy/refunds" }] },

  // ---------------- Returns & Issues ----------------
  { id: "rt-policy", category: "Returns & Issues", active: true,
    q: "What is the return policy?",
    a: "Our full return and refund terms are documented on the Refund Policy page, which is the authoritative source for eligibility and process.",
    keywords: ["return", "refund", "policy", "money back"],
    links: [{ label: "Refund Policy", to: "/policy/refunds" }] },
  { id: "rt-canreturn", category: "Returns & Issues", active: true,
    q: "Can research materials be returned?",
    a: "Because these are sensitive research materials, returns are handled according to our Refund Policy. Please review it and contact support with any specific situation.",
    keywords: ["return materials", "can i return", "send back"],
    links: [{ label: "Refund Policy", to: "/policy/refunds" }] },
  { id: "rt-wrong", category: "Returns & Issues", active: true,
    q: "What should I do if I receive the wrong product?",
    a: "Contact support with your order reference and details of what you received versus what you ordered, and we'll make it right.",
    keywords: ["wrong", "incorrect", "mistake", "mixed up"],
    links: [{ label: "Contact Support", to: "/contact" }] },
  { id: "rt-vial", category: "Returns & Issues", active: true,
    q: "What should I do if a vial arrives damaged?",
    a: "Reach out to support promptly with your order reference and photos of the packaging and vial so we can review it under our Refund Policy.",
    keywords: ["vial damaged", "broken vial", "cracked"],
    links: [{ label: "Contact Support", to: "/contact" }] },
  { id: "rt-report", category: "Returns & Issues", active: true,
    q: "How do I report an order issue?",
    a: "Use the Contact page or reply to your order confirmation with your order reference and a description of the issue. Photos help us resolve problems faster.",
    keywords: ["report", "issue", "problem", "complaint"],
    links: [{ label: "Contact Support", to: "/contact" }] },

  // ---------------- Account & Privacy ----------------
  { id: "ac-privacy", category: "Account & Privacy", active: true,
    q: "How is my information handled?",
    a: "We handle personal information in accordance with our Privacy Policy. Only the information needed to process your order and support your account is collected.",
    keywords: ["privacy", "data", "information", "personal"],
    links: [{ label: "Privacy Policy", to: "/policy/privacy" }] },
  { id: "ac-collect", category: "Account & Privacy", active: true,
    q: "What information is collected during ordering?",
    a: "Ordering collects the details required to fulfil and confirm your order, such as your email and shipping information. See our Privacy Policy for specifics.",
    keywords: ["collect", "data collected", "what information"],
    links: [{ label: "Privacy Policy", to: "/policy/privacy" }] },
  { id: "ac-support", category: "Account & Privacy", active: true,
    q: "How do I get help with my account or an order?",
    a: "Sign in to view your order history and reorders, or contact support with your order reference for assistance.",
    keywords: ["account help", "order help", "assistance"],
    links: [{ label: "Your Account", to: "/account" }, { label: "Contact Support", to: "/contact" }] },

  // ---------------- Compliance ----------------
  { id: "cm-restrict", category: "Compliance", active: true,
    q: "Why does Molecularix restrict product use?",
    a: "Our materials are reference compounds for in-vitro research. Restricting use to research applications reflects what these materials are and keeps our positioning clear and compliant.",
    keywords: ["restrict", "compliance", "why", "limits"],
    links: [{ label: "Research Use Only Policy", to: "/policy/research-use" }] },
  { id: "cm-noclaims", category: "Compliance", active: true,
    q: "Why are human-use claims not provided?",
    a: "Our products are not drugs and are not intended for human or animal use, so we do not make efficacy, therapeutic, or usage claims and do not provide dosing or administration guidance.",
    keywords: ["claims", "human use", "no dosing", "efficacy"],
    links: [{ label: "Terms & Conditions", to: "/policy/terms" }] },
  { id: "cm-responsibility", category: "Compliance", active: true,
    q: "What responsibilities does the purchaser have?",
    a: "Purchasers are responsible for handling, storing, and using materials lawfully and safely within an appropriate research context, and for complying with all applicable laws in their jurisdiction.",
    keywords: ["responsibility", "purchaser", "buyer", "obligations"],
    links: [{ label: "Terms & Conditions", to: "/policy/terms" }] },
  { id: "cm-legit", category: "Compliance", active: true,
    q: "What does Molecularix mean by legitimate research use?",
    a: "Legitimate research use means in-vitro laboratory work conducted by qualified researchers, without any human or veterinary application. This is the only intended use of our materials.",
    keywords: ["legitimate", "research use", "definition", "meaning"],
    links: [{ label: "Our Standards", to: "/standards" }] },

  // ---------------- Institutional / Wholesale ----------------
  { id: "iw-support", category: "Institutional / Wholesale", active: true,
    q: "Does Molecularix support institutional research orders?",
    a: "For institutional or laboratory purchasing inquiries, contact us and we'll discuss how we can help. We tailor support to the needs of research accounts where possible.",
    keywords: ["institutional", "lab", "university", "organization"],
    links: [{ label: "Contact Us", to: "/contact" }] },
  { id: "iw-larger", category: "Institutional / Wholesale", active: true,
    q: "Are larger or bulk orders available?",
    a: "If you require larger quantities than shown, reach out and we'll let you know what we can accommodate.",
    keywords: ["bulk", "larger", "wholesale", "quantity", "volume"],
    links: [{ label: "Contact Us", to: "/contact" }] },
  { id: "iw-contact", category: "Institutional / Wholesale", active: true,
    q: "How can a laboratory or organization contact Molecularix?",
    a: "Use the Contact page or email us, and note that you're inquiring on behalf of a laboratory or organization so we can route your request appropriately.",
    keywords: ["contact lab", "organization", "reach out"],
    links: [{ label: "Contact Us", to: "/contact" }] },

  // ---------------- Contact & Support ----------------
  { id: "cs-contact", category: "Contact & Support", active: true,
    q: "How do I contact Molecularix Biolabs?",
    a: "You can reach us through the Contact page, by email at MolecularixBiolabs@gmail.com, or via WhatsApp for quick questions.",
    keywords: ["contact", "email", "reach", "support", "whatsapp"],
    links: [{ label: "Contact Support", to: "/contact" }] },
  { id: "cs-response", category: "Contact & Support", active: true,
    q: "How quickly will support respond?",
    a: "We aim to respond as promptly as possible during business hours. Including your order reference and clear details helps us resolve requests faster.",
    keywords: ["response", "how fast", "reply", "time"],
    links: [{ label: "Contact Support", to: "/contact" }] },
  { id: "cs-docs", category: "Contact & Support", active: true,
    q: "Where can I get help with documentation?",
    a: "Start at the Documentation Center to request specifications or batch documentation. If you need further help, contact support with the compound or SKU.",
    keywords: ["documentation help", "docs"],
    links: [{ label: "Documentation Center", to: "/documentation" }] },
];

export const activeFaqs = () => FAQS.filter((f) => f.active !== false);
